import { delivery, type audience, type contentTemplates, type deliveryControls } from '@bb/contracts';
import { pruneSources } from '../../../templates/templateContent.ts';
import { deliveryControlsIssues } from '../../../campaigns/schedule/deliveryControls.ts';
import type { PushComposerValue } from '../../../composer/push/pushDraft.ts';

/**
 * Message step settings as the editor reads and writes them (BB-115): the shape of `MessageStepConfig` in `@bb/domain`
 * (`steps/message.ts`). Channel content is what a campaign variant holds (push: `PushContentInput`, card: the
 * `content_card` channel content of BB-63/BB-64).
 */
export type DeliveryChannel = delivery.DeliveryChannel;
export type Policy = 'advance' | 'exit';
export type ChannelContent = Record<string, unknown>;
export type QuietHours = deliveryControls.QuietHours;

export interface MessageConfig {
  channels: DeliveryChannel[];
  content: Partial<Record<DeliveryChannel, ChannelContent>>;
  /** The template version a channel's content was copied from (BB-56). */
  sourceTemplates?: Partial<Record<DeliveryChannel, contentTemplates.TemplateSource>>;
  validateAudienceAtSend?: 'canvas_audience' | audience.AudienceDefinition;
  onValidationFailure: Policy;
  onSuppressed: Policy;
  onFailed: Policy;
  overrides?: { quietHours?: QuietHours; exemptions?: deliveryControls.MessageExemptions };
}

/** Channels a Canvas Message step offers (web push stays with campaigns). */
export const CANVAS_CHANNELS = ['push_android', 'push_ios', 'content_card', 'in_app', 'webhook'] as const satisfies readonly DeliveryChannel[];
export const PUSH_CHANNELS = ['push_android', 'push_ios'] as const satisfies readonly DeliveryChannel[];
export const CARD_EXPIRY_MAX_SECONDS = 30 * 86_400;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const policy = (v: unknown, fallback: Policy): Policy => (v === 'advance' || v === 'exit' ? v : fallback);
const isPush = (c: DeliveryChannel) => (PUSH_CHANNELS as readonly string[]).includes(c);
/** Channels in the order campaigns list them, each once. */
const ordered = (channels: Iterable<DeliveryChannel>) => {
  const set = new Set(channels);
  return delivery.DELIVERY_CHANNELS.filter((c) => set.has(c));
};

/** The stored settings with the server's defaults filled in and unknown keys left out (an older design-time shape repairs itself on the first edit). */
export function readMessage(raw: unknown): MessageConfig {
  const c = isObject(raw) ? raw : {};
  const content = isObject(c.content) ? c.content : {};
  const channels = ordered((Array.isArray(c.channels) ? c.channels : []).filter((ch): ch is DeliveryChannel => (delivery.DELIVERY_CHANNELS as readonly unknown[]).includes(ch)));
  const overrides = isObject(c.overrides) ? (c.overrides as MessageConfig['overrides']) : undefined;
  const sources = isObject(c.sourceTemplates) ? pruneSources(c.sourceTemplates as MessageConfig['sourceTemplates'], channels) : undefined;
  return {
    channels,
    content: Object.fromEntries(channels.map((ch) => [ch, isObject(content[ch]) ? content[ch] : {}])),
    ...(sources ? { sourceTemplates: sources } : {}),
    ...(c.validateAudienceAtSend === 'canvas_audience' || isObject(c.validateAudienceAtSend) ? { validateAudienceAtSend: c.validateAudienceAtSend as MessageConfig['validateAudienceAtSend'] } : {}),
    onValidationFailure: policy(c.onValidationFailure, 'exit'),
    onSuppressed: policy(c.onSuppressed, 'advance'),
    onFailed: policy(c.onFailed, 'advance'),
    ...(overrides && Object.keys(overrides).length ? { overrides } : {}),
  };
}

/** A new card: classic, no action, dismissible; created at first impression and kept 30 days (BB-63, BB-64 defaults). */
export const newCardContent = (): ChannelContent => ({
  card: { type: 'classic', action: { kind: 'none' }, pinned: false, dismissible: true, extras: {} },
  creationMode: 'first_impression',
  expiry: { mode: 'duration', seconds: CARD_EXPIRY_MAX_SECONDS },
});

/** A channel on (with empty content, or a new card) or off (its content goes: the pipeline refuses content of a channel the message does not use). */
export function setChannel(c: MessageConfig, channel: DeliveryChannel, on: boolean): MessageConfig {
  if (on === c.channels.includes(channel)) return c;
  if (on) return { ...c, channels: ordered([...c.channels, channel]), content: { ...c.content, [channel]: channel === 'content_card' ? newCardContent() : {} } };
  const { [channel]: _gone, ...content } = c.content;
  const { sourceTemplates, ...rest } = c;
  const channels = c.channels.filter((ch) => ch !== channel);
  const kept = pruneSources(sourceTemplates, channels);
  return { ...rest, channels, content, ...(kept ? { sourceTemplates: kept } : {}) };
}

/** The push composer's value: the push channels and their content. */
export const pushValue = (c: MessageConfig): PushComposerValue => Object.fromEntries(PUSH_CHANNELS.filter((ch) => c.channels.includes(ch)).map((ch) => [ch, c.content[ch] ?? {}]));

/** The push channels as the composer wrote them (a platform switched off leaves its channel out). */
export function setPush(c: MessageConfig, value: PushComposerValue | null): MessageConfig {
  const others = c.channels.filter((ch) => !isPush(ch));
  const content = Object.fromEntries(Object.entries(c.content).filter(([ch]) => !isPush(ch as DeliveryChannel)));
  const push = value ? PUSH_CHANNELS.filter((ch) => value[ch]) : [];
  return { ...c, channels: ordered([...others, ...push]), content: { ...content, ...Object.fromEntries(push.map((ch) => [ch, value![ch] as ChannelContent])) } };
}

export const setContent = (c: MessageConfig, channel: DeliveryChannel, next: ChannelContent): MessageConfig => ({ ...c, content: { ...c.content, [channel]: next } });

/** Quiet hours of this step, or none (the override object is left out when empty). */
export function setQuietHours(c: MessageConfig, quietHours: QuietHours | undefined): MessageConfig {
  const { quietHours: _old, ...rest } = c.overrides ?? {};
  const overrides = quietHours ? { ...rest, quietHours } : rest;
  const { overrides: _o, ...base } = c;
  return Object.keys(overrides).length ? { ...base, overrides } : base;
}

/** The schema's rules as messages per field (`channels`, `quietHours.from`, `quietHours.to`). */
export function messageIssues(c: MessageConfig): Record<string, string> {
  const issues: Record<string, string> = {};
  if (c.channels.length === 0) issues.channels = 'Choose at least one channel.';
  if (c.overrides?.quietHours) Object.assign(issues, deliveryControlsIssues({ quietHours: c.overrides.quietHours }));
  return issues;
}

// ---------------------------------------------------------------------------------------------------------------
// Content Card (the card before templating, BB-61; creation BB-63; expiry BB-64)

export const CARD_TYPES = [
  { value: 'classic', label: 'Classic' },
  { value: 'captioned_image', label: 'Captioned image' },
  { value: 'image_only', label: 'Image only' },
] as const;

export interface CardFields {
  type: string;
  title: string;
  description: string;
  imageUrl: string;
  aspectRatio: number | undefined;
}

const cardOf = (content: ChannelContent): Record<string, unknown> => (isObject(content.card) ? content.card : {});

export function cardFields(content: ChannelContent): CardFields {
  const card = cardOf(content);
  const image = isObject(card.image) ? card.image : {};
  return {
    type: typeof card.type === 'string' ? card.type : 'classic',
    title: typeof card.title === 'string' ? card.title : '',
    description: typeof card.description === 'string' ? card.description : '',
    imageUrl: typeof image.url === 'string' ? image.url : '',
    aspectRatio: typeof image.aspectRatio === 'number' ? image.aspectRatio : undefined,
  };
}

/** A card field set, or left out when empty; what the editor does not show (action, pin, extras) is kept. */
export function setCardField(content: ChannelContent, field: keyof CardFields, value: string | number | undefined): ChannelContent {
  const card: Record<string, unknown> = { ...cardOf(content) };
  if (field === 'imageUrl' || field === 'aspectRatio') {
    const image: Record<string, unknown> = { ...(isObject(card.image) ? card.image : {}) };
    const key = field === 'imageUrl' ? 'url' : 'aspectRatio';
    if (value === '' || value === undefined) delete image[key];
    else image[key] = value;
    if (Object.keys(image).length) card.image = image;
    else delete card.image;
  } else if (value === '' || value === undefined) delete card[field];
  else card[field] = value;
  return { ...content, card };
}

/** What a card of its type needs (the SDK card contract): messages per field. */
export function cardIssues(content: ChannelContent): Record<string, string> {
  const f = cardFields(content);
  const issues: Record<string, string> = {};
  const needsImage = f.type === 'captioned_image' || f.type === 'image_only';
  if (f.type === 'classic' && !f.title && !f.description) issues.title = 'A classic card needs a title or a description.';
  if (f.type === 'captioned_image' && !f.title) issues.title = 'A captioned image card needs a title.';
  if (f.title.length > 500) issues.title = 'At most 500 characters.';
  if (f.description.length > 2000) issues.description = 'At most 2,000 characters.';
  if (needsImage && !f.imageUrl) issues.imageUrl = 'This card type needs an image.';
  else if (f.imageUrl && !/^https:\/\/\S+$/.test(f.imageUrl)) issues.imageUrl = 'Must be an https:// URL.';
  if (f.imageUrl && (f.aspectRatio === undefined || !(f.aspectRatio > 0) || f.aspectRatio > 10)) issues.aspectRatio = 'Enter width ÷ height of the image, above 0 and up to 10.';
  const expiry = isObject(content.expiry) ? content.expiry : undefined;
  if (expiry?.mode === 'duration' && (!Number.isInteger(expiry.seconds) || (expiry.seconds as number) < 60 || (expiry.seconds as number) > CARD_EXPIRY_MAX_SECONDS)) issues.expiry = 'Between 1 minute and 30 days.';
  if (expiry?.mode === 'at' && (typeof expiry.at !== 'string' || Number.isNaN(Date.parse(expiry.at)))) issues.expiry = 'Choose a date and time.';
  return issues;
}
